import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarChart3, DollarSign, Receipt, History } from 'lucide-react';
import FinancialTabsNav from '../components/financial/FinancialTabsNav';
import OverviewTab from '../components/financial/OverviewTab';
import RevenueTab from '../components/financial/RevenueTab';
import CostsTab from '../components/financial/CostsTab';
import PaymentsTab from '../components/financial/PaymentsTab';
import AddPaymentModal from '../components/financial/AddPaymentModal';
import EditPaymentModal from '../components/financial/EditPaymentModal';
import ViewPaymentModal from '../components/financial/ViewPaymentModal';
import paymentService from '../services/paymentService';
import { useFinancialRefresh } from '../contexts/FinancialRefreshContext';
import FinancialNotification from '../components/ui/FinancialNotification';

const getDefaultPaymentForm = () => ({
  recipientName: '',
  recipientType: 'driver',
  paymentType: 'weekly_settlement',
  paymentCalculationType: 'percentage',
  payPeriodStart: '',
  payPeriodEnd: '',
  paymentDate: '',
  paymentMethod: 'direct_deposit',
  status: 'pending',
  loadCount: 0,
  totalMiles: 0,
  grossRevenue: 0,
  netRevenue: 0,
  loadAmount: 0,
  percentageRate: 65,
  salaryAmount: 0,
  mileageRate: 0.6,
  commissionRate: 10,
  basePay: 0,
  deductions: {
    fuel: 0,
    advances: 0,
    insurance: 0,
    taxes: 0,
    toll: 0,
    prepass: 0,
    eld: 0,
    retirement: 0,
    garnishment: 0,
    damages: 0,
    other: 0
  },
  bonuses: {
    safety: 0,
    onTime: 0,
    efficiency: 0,
    performance: 0,
    referral: 0,
    other: 0
  },
  notes: '',
  attachments: []
});

const buildSummaryFromStats = (stats, fallback = {}) => {
  if (!stats) {
    return fallback;
  }

  const driverStats = stats.byRecipientType?.driver || {};
  const dispatcherStats = stats.byRecipientType?.dispatcher || {};
  const totalPaid = Number(stats.totalPaid) || 0;
  const driverAmount = Number(driverStats.amount) || 0;
  const dispatcherAmount = Number(dispatcherStats.amount) || 0;
  const pendingAmount = Number(stats.totalPending) || 0;

  return {
    thisWeek: {
      totalPaid,
      driverPayments: driverAmount,
      dispatcherPayments: dispatcherAmount,
      pendingPayments: pendingAmount
    },
    thisMonth: {
      totalPaid,
      driverPayments: driverAmount,
      dispatcherPayments: dispatcherAmount,
      avgDriverPay: driverStats.count ? driverAmount / driverStats.count : 0,
      avgDispatcherPay: dispatcherStats.count ? dispatcherAmount / dispatcherStats.count : 0
    },
    yearToDate: {
      totalPaid,
      driverPayments: driverAmount,
      dispatcherPayments: dispatcherAmount,
      totalBonuses: Number(stats.totalBonuses) || 0,
      totalDeductions: Number(stats.totalDeductions) || 0
    }
  };
};

const FinancialDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get tab from URL query parameter, default to 'overview'
  const tabFromUrl = searchParams.get('tab') || 'overview';
  const validTabs = ['overview', 'revenue', 'costs', 'payments'];
  const initialTab = validTabs.includes(tabFromUrl) ? tabFromUrl : 'overview';

  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const { onPaymentCreated, onPaymentUpdated, onPaymentDeleted, refreshTriggers } = useFinancialRefresh();
  const [financialNotification, setFinancialNotification] = useState(null);
  const [paymentFilters, setPaymentFilters] = useState({
    search: '',
    recipientType: 'all',
    status: 'all',
    paymentMethod: 'all',
    calculationType: 'all',
    startDate: '',
    endDate: ''
  });

  // Handle tab change and update URL
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  }, [setSearchParams]);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
  const [showViewPaymentModal, setShowViewPaymentModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [viewingPayment, setViewingPayment] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [newPayment, setNewPayment] = useState(() => getDefaultPaymentForm());
  const [financialData, setFinancialData] = useState(() => ({
    overview: {
      totalRevenue: 87500,
      totalExpenses: 62300,
      netProfit: 25200,
      profitMargin: 28.8,
      pendingInvoices: 3,
      totalLoads: 45,
      avgLoadValue: 1944,
      dispatchEfficiency: 92.5,
      fuelEfficiency: 6.8,
      utilizationRate: 85.2,
      avgDeliveryTime: 2.3,
      customerSatisfaction: 4.6
    },
    recentLoads: [
      { loadNumber: 'LD-2024-001', route: 'Chicago to Atlanta', date: '2024-11-03', revenue: 2850 },
      { loadNumber: 'LD-2024-002', route: 'Atlanta to Miami', date: '2024-11-04', revenue: 1950 },
      { loadNumber: 'LD-2024-003', route: 'Miami to Tampa', date: '2024-11-05', revenue: 1200 }
    ],
    recentExpenses: [
      { category: 'Fuel', description: 'Shell Station - I-75', date: '2024-11-03', amount: 450 },
      { category: 'Maintenance', description: 'Oil Change - Truck 001', date: '2024-11-02', amount: 85 },
      { category: 'Fuel', description: 'BP Station - I-95', date: '2024-11-04', amount: 380 }
    ],
    driverSettlements: [
      { driverName: 'John Smith', period: 'Nov 1-7', amount: 2100, status: 'Pending' },
      { driverName: 'Sarah Johnson', period: 'Nov 1-7', amount: 1850, status: 'Processed' }
    ],
    vehicleProfitability: [
      { vehicleId: 'Truck-001', revenue: 28500, expenses: 18200, profit: 10300, margin: 36.1 },
      { vehicleId: 'Truck-002', revenue: 31200, expenses: 20800, profit: 10400, margin: 33.3 },
      { vehicleId: 'Truck-003', revenue: 27800, expenses: 23100, profit: 4700, margin: 16.9 }
    ],
    monthlyTrends: {
      '3months': [
        { month: 'Sep', revenue: 85200, expenses: 60100, profit: 25100 },
        { month: 'Oct', revenue: 87500, expenses: 62300, profit: 25200 },
        { month: 'Nov', revenue: 89100, expenses: 63800, profit: 25300 }
      ],
      '6months': [
        { month: 'Jun', revenue: 78000, expenses: 55200, profit: 22800 },
        { month: 'Jul', revenue: 80500, expenses: 56800, profit: 23700 },
        { month: 'Aug', revenue: 82000, expenses: 58500, profit: 23500 },
        { month: 'Sep', revenue: 85200, expenses: 60100, profit: 25100 },
        { month: 'Oct', revenue: 87500, expenses: 62300, profit: 25200 },
        { month: 'Nov', revenue: 89100, expenses: 63800, profit: 25300 }
      ],
      '12months': [
        { month: 'Dec 23', revenue: 65000, expenses: 48500, profit: 16500 },
        { month: 'Jan', revenue: 68200, expenses: 50100, profit: 18100 },
        { month: 'Feb', revenue: 71000, expenses: 51800, profit: 19200 },
        { month: 'Mar', revenue: 73500, expenses: 53200, profit: 20300 },
        { month: 'Apr', revenue: 75800, expenses: 54100, profit: 21700 },
        { month: 'May', revenue: 77200, expenses: 54800, profit: 22400 },
        { month: 'Jun', revenue: 78000, expenses: 55200, profit: 22800 },
        { month: 'Jul', revenue: 80500, expenses: 56800, profit: 23700 },
        { month: 'Aug', revenue: 82000, expenses: 58500, profit: 23500 },
        { month: 'Sep', revenue: 85200, expenses: 60100, profit: 25100 },
        { month: 'Oct', revenue: 87500, expenses: 62300, profit: 25200 },
        { month: 'Nov', revenue: 89100, expenses: 63800, profit: 25300 }
      ]
    },
    costBreakdown: [
      { name: 'Fuel', value: 28500, color: '#ef4444', perMile: 0.48, trend: '+3.2%' },
      { name: 'Driver Pay', value: 18200, color: '#22c55e', perMile: 0.31, trend: '+5.1%' },
      { name: 'Maintenance', value: 8900, color: '#f97316', perMile: 0.15, trend: '-2.1%' },
      { name: 'Insurance', value: 4200, color: '#3b82f6', perMile: 0.07, trend: '+1.2%' },
      { name: 'Other', value: 2500, color: '#8b5cf6', perMile: 0.04, trend: '-0.5%' }
    ],
    driverPerformance: [
      { 
        driverId: 'DRV-001', 
        name: 'John Smith', 
        totalRevenue: 32500, 
        totalCosts: 21200, 
        netProfit: 11300, 
        profitMargin: 34.8,
        totalMiles: 28500,
        totalLoads: 18,
        avgRevenuePerMile: 1.14,
        fuelEfficiency: 7.2,
        onTimeRate: 96.5,
        safetyScore: 98,
        customerRating: 4.8
      },
      { 
        driverId: 'DRV-002', 
        name: 'Sarah Johnson', 
        totalRevenue: 30100, 
        totalCosts: 20800, 
        netProfit: 9300, 
        profitMargin: 30.9,
        totalMiles: 26800,
        totalLoads: 16,
        avgRevenuePerMile: 1.12,
        fuelEfficiency: 6.9,
        onTimeRate: 94.2,
        safetyScore: 95,
        customerRating: 4.6
      },
      { 
        driverId: 'DRV-003', 
        name: 'Mike Wilson', 
        totalRevenue: 24900, 
        totalCosts: 20300, 
        netProfit: 4600, 
        profitMargin: 18.5,
        totalMiles: 23200,
        totalLoads: 11,
        avgRevenuePerMile: 1.07,
        fuelEfficiency: 6.1,
        onTimeRate: 89.5,
        safetyScore: 88,
        customerRating: 4.2
      }
    ],
    routeAnalysis: [
      { route: 'Chicago-Atlanta', loads: 8, avgRevenue: 2850, avgCost: 1920, profit: 930, margin: 32.6, frequency: 'Weekly' },
      { route: 'Atlanta-Miami', loads: 6, avgRevenue: 1950, avgCost: 1420, profit: 530, margin: 27.2, frequency: 'Bi-weekly' },
      { route: 'Miami-Tampa', loads: 12, avgRevenue: 1200, avgCost: 890, profit: 310, margin: 25.8, frequency: 'Daily' },
      { route: 'Tampa-Orlando', loads: 9, avgRevenue: 950, avgCost: 720, profit: 230, margin: 24.2, frequency: 'Daily' }
    ],
    costOptimization: [
      { category: 'Fuel', currentCost: 28500, optimizedCost: 26200, savings: 2300, recommendation: 'Route optimization & fuel cards' },
      { category: 'Maintenance', currentCost: 8900, optimizedCost: 7800, savings: 1100, recommendation: 'Preventive maintenance scheduling' },
      { category: 'Driver Pay', currentCost: 18200, optimizedCost: 17500, savings: 700, recommendation: 'Performance-based incentives' }
    ],
    paymentHistory: [
      {
        id: 'PAY-2024-001',
        recipientName: 'John Smith',
        recipientType: 'driver',
        paymentType: 'weekly_settlement',
        paymentCalculationType: 'percentage',
        loadAmount: 8500,
        percentageRate: 65,
        calculatedBasePay: 5525,
        amount: 2150,
        payPeriodStart: '2024-11-01',
        payPeriodEnd: '2024-11-07',
        paymentDate: '2024-11-08',
        status: 'paid',
        paymentMethod: 'direct_deposit',
        loadCount: 8,
        totalMiles: 3250,
        grossRevenue: 12400,
        deductions: {
          fuel: 850,
          advances: 200,
          insurance: 150,
          toll: 75,
          prepass: 25,
          eld: 15,
          other: 50
        },
        bonuses: {
          safety: 100,
          onTime: 50,
          efficiency: 75
        }
      },
      {
        id: 'PAY-2024-002',
        recipientName: 'Sarah Johnson',
        recipientType: 'driver',
        paymentType: 'weekly_settlement',
        paymentCalculationType: 'mileage',
        totalMiles: 2890,
        mileageRate: 0.58,
        calculatedBasePay: 1676,
        amount: 1875,
        payPeriodStart: '2024-11-01',
        payPeriodEnd: '2024-11-07',
        paymentDate: '2024-11-08',
        status: 'paid',
        paymentMethod: 'check',
        loadCount: 6,
        grossRevenue: 10200,
        deductions: {
          fuel: 720,
          advances: 0,
          insurance: 150,
          toll: 45,
          prepass: 25,
          eld: 15,
          other: 25
        },
        bonuses: {
          safety: 100,
          onTime: 0,
          efficiency: 50
        }
      },
      {
        id: 'PAY-2024-003',
        recipientName: 'Mike Wilson',
        recipientType: 'owner_operator',
        paymentType: 'weekly_settlement',
        paymentCalculationType: 'owner_operator',
        loadAmount: 9500,
        commissionRate: 12,
        commission: 1140,
        calculatedBasePay: 8360,
        amount: 7930,
        payPeriodStart: '2024-11-01',
        payPeriodEnd: '2024-11-07',
        paymentDate: '2024-11-08',
        status: 'pending',
        paymentMethod: 'direct_deposit',
        loadCount: 4,
        totalMiles: 2100,
        grossRevenue: 7800,
        deductions: {
          fuel: 350,
          advances: 80,
          insurance: 0,
          toll: 30,
          prepass: 25,
          eld: 15,
          other: 0
        },
        bonuses: {
          safety: 0,
          onTime: 0,
          efficiency: 0
        }
      },
      {
        id: 'PAY-2024-004',
        recipientName: 'Lisa Rodriguez',
        recipientType: 'dispatcher',
        paymentType: 'bi_weekly_salary',
        paymentCalculationType: 'flat_rate',
        salaryAmount: 3200,
        calculatedBasePay: 3200,
        amount: 2800,
        payPeriodStart: '2024-10-28',
        payPeriodEnd: '2024-11-08',
        paymentDate: '2024-11-09',
        status: 'paid',
        paymentMethod: 'direct_deposit',
        loadCount: 35,
        totalMiles: 0,
        grossRevenue: 68500,
        deductions: {
          taxes: 420,
          insurance: 180,
          retirement: 140,
          other: 60
        },
        bonuses: {
          performance: 200,
          efficiency: 150
        }
      },
      {
        id: 'PAY-2024-005',
        recipientName: 'David Chen',
        recipientType: 'dispatcher',
        paymentType: 'bi_weekly_salary',
        amount: 2650,
        payPeriodStart: '2024-10-28',
        payPeriodEnd: '2024-11-08',
        paymentDate: '2024-11-09',
        status: 'paid',
        paymentMethod: 'direct_deposit',
        loadCount: 28,
        totalMiles: 0,
        grossRevenue: 52300,
        deductions: {
          taxes: 398,
          insurance: 180,
          retirement: 132,
          other: 40
        },
        bonuses: {
          performance: 150,
          efficiency: 100
        }
      },
      {
        id: 'PAY-2024-006',
        recipientName: 'John Smith',
        recipientType: 'driver',
        paymentType: 'weekly_settlement',
        amount: 2350,
        payPeriodStart: '2024-10-25',
        payPeriodEnd: '2024-10-31',
        paymentDate: '2024-11-01',
        status: 'paid',
        paymentMethod: 'direct_deposit',
        loadCount: 9,
        totalMiles: 3580,
        grossRevenue: 13700,
        deductions: {
          fuel: 920,
          advances: 100,
          insurance: 150,
          other: 30
        },
        bonuses: {
          safety: 100,
          onTime: 100,
          efficiency: 100
        }
      }
    ],
    paymentSummary: {
      thisWeek: {
        totalPaid: 14095,
        driverPayments: 5445,
        dispatcherPayments: 5450,
        pendingPayments: 1420
      },
      thisMonth: {
        totalPaid: 58420,
        driverPayments: 35680,
        dispatcherPayments: 22740,
        avgDriverPay: 1784,
        avgDispatcherPay: 2725
      },
      yearToDate: {
        totalPaid: 642580,
        driverPayments: 425840,
        dispatcherPayments: 216740,
        totalBonuses: 18650,
        totalDeductions: 125480
      }
    },
    insights: [
      {
        type: 'opportunity',
        title: 'Route Optimization Potential',
        description: 'Chicago-Atlanta route shows 15% higher profit margin. Consider increasing frequency.',
        impact: '$3,200/month',
        priority: 'high'
      },
      {
        type: 'warning',
        title: 'Driver Performance Gap',
        description: 'Mike Wilson\'s efficiency is 18% below team average. Training recommended.',
        impact: '$1,800/month',
        priority: 'medium'
      },
      {
        type: 'success',
        title: 'Fuel Efficiency Improvement',
        description: 'Overall fuel efficiency improved by 8% this quarter.',
        impact: '$2,100 saved',
        priority: 'low'
      }
    ]
  }));
  const [paymentQuery, setPaymentQuery] = useState({ page: 1, limit: 25 });
  const [paymentsPagination, setPaymentsPagination] = useState({
    page: 1,
    limit: 25,
    totalCount: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false
  });
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [paymentsError, setPaymentsError] = useState(null);
  const [pendingPayments, setPendingPayments] = useState({
    payments: [],
    totalPending: 0,
    count: 0
  });
  const [isSavingPayment, setIsSavingPayment] = useState(false);

  const fetchPaymentsData = useCallback(async () => {
    setPaymentsLoading(true);
    setPaymentsError(null);

    const filtersForApi = {
      recipientType: paymentFilters.recipientType !== 'all' ? paymentFilters.recipientType : undefined,
      status: paymentFilters.status !== 'all' ? paymentFilters.status : undefined,
      paymentMethod: paymentFilters.paymentMethod !== 'all' ? paymentFilters.paymentMethod : undefined,
      paymentCalculationType:
        paymentFilters.calculationType &&
        paymentFilters.calculationType !== 'all' &&
        paymentFilters.calculationType !== 'legacy'
          ? paymentFilters.calculationType
          : undefined,
      paymentDateStart: paymentFilters.startDate || undefined,
      paymentDateEnd: paymentFilters.endDate || undefined,
      page: paymentQuery.page,
      limit: paymentQuery.limit
    };

    try {
      const listResult = await paymentService.listPayments(filtersForApi);
      const [statsResult, pendingResult] = await Promise.allSettled([
        paymentService.getPaymentStatistics({
          startDate: filtersForApi.paymentDateStart,
          endDate: filtersForApi.paymentDateEnd
        }),
        paymentService.getPendingPayments()
      ]);

      setFinancialData(prev => ({
        ...prev,
        paymentHistory: listResult.payments,
        paymentSummary:
          statsResult.status === 'fulfilled'
            ? buildSummaryFromStats(statsResult.value, prev.paymentSummary)
            : prev.paymentSummary
      }));

      if (pendingResult.status === 'fulfilled') {
        setPendingPayments(pendingResult.value);
      }

      setPaymentsPagination(listResult.pagination);
    } catch (error) {
      console.error('Failed to fetch payments:', error);
      const message = typeof error.getUserFriendlyMessage === 'function'
        ? error.getUserFriendlyMessage()
        : error.message || 'Unable to load payments';
      setPaymentsError(message);
    } finally {
      setPaymentsLoading(false);
      setLoading(false);
    }
  }, [
    paymentFilters.recipientType,
    paymentFilters.status,
    paymentFilters.paymentMethod,
    paymentFilters.calculationType,
    paymentFilters.startDate,
    paymentFilters.endDate,
    paymentQuery.page,
    paymentQuery.limit
  ]);

  useEffect(() => {
    fetchPaymentsData();
  }, [fetchPaymentsData]);

  useEffect(() => {
    setPaymentQuery(prev => {
      if (prev.page === 1) {
        return prev;
      }
      return {
        ...prev,
        page: 1
      };
    });
  }, [
    paymentFilters.recipientType,
    paymentFilters.status,
    paymentFilters.paymentMethod,
    paymentFilters.calculationType,
    paymentFilters.startDate,
    paymentFilters.endDate
  ]);

  const handlePaymentsPageChange = (page) => {
    setPaymentQuery(prev => {
      if (page === prev.page) {
        return prev;
      }
      return {
        ...prev,
        page: Math.max(1, page)
      };
    });
  };


  const getFilteredPaymentData = () => {
    if (!Array.isArray(financialData.paymentHistory)) {
      return [];
    }
    
    return financialData.paymentHistory.filter(payment => {
      // Search filter
      if (paymentFilters.search) {
        const searchTerm = paymentFilters.search.toLowerCase();
        const fieldsToSearch = [payment.recipientName, payment.id, payment.paymentNumber]
          .filter(Boolean)
          .map(field => field.toLowerCase());

        if (!fieldsToSearch.some(field => field.includes(searchTerm))) {
          return false;
        }
      }
      
      // Recipient type filter
      if (paymentFilters.recipientType !== 'all' && payment.recipientType !== paymentFilters.recipientType) {
        return false;
      }
      
      // Status filter
      if (paymentFilters.status !== 'all' && payment.status !== paymentFilters.status) {
        return false;
      }
      
      // Payment method filter
      if (paymentFilters.paymentMethod !== 'all' && payment.paymentMethod !== paymentFilters.paymentMethod) {
        return false;
      }

      // Calculation type filter
      if (paymentFilters.calculationType && paymentFilters.calculationType !== 'all') {
        if (paymentFilters.calculationType === 'legacy' && payment.paymentCalculationType) {
          return false;
        }
        if (paymentFilters.calculationType !== 'legacy' && payment.paymentCalculationType !== paymentFilters.calculationType) {
          return false;
        }
      }
      
      const paymentDateValue = payment.paymentDate || payment.payDate || '';

      if (paymentFilters.startDate) {
        if (!paymentDateValue || new Date(paymentDateValue) < new Date(paymentFilters.startDate)) {
          return false;
        }
      }
      
      if (paymentFilters.endDate) {
        if (!paymentDateValue || new Date(paymentDateValue) > new Date(paymentFilters.endDate)) {
          return false;
        }
      }
      
      return true;
    });
  };

  const calculateBasePay = () => {
    const { paymentCalculationType } = newPayment;
    
    switch (paymentCalculationType) {
      case 'percentage':
        // Driver gets percentage of load amount
        const loadAmount = parseFloat(newPayment.loadAmount) || 0;
        const percentageRate = parseFloat(newPayment.percentageRate) || 0;
        return loadAmount * (percentageRate / 100);
        
      case 'flat_rate':
        // Fixed salary amount
        return parseFloat(newPayment.salaryAmount) || 0;
        
      case 'mileage':
        // Payment based on miles driven
        const totalMiles = parseFloat(newPayment.totalMiles) || 0;
        const mileageRate = parseFloat(newPayment.mileageRate) || 0;
        return totalMiles * mileageRate;
        
      case 'owner_operator':
        // Load amount minus commission
        const ownerLoadAmount = parseFloat(newPayment.loadAmount) || 0;
        const commissionRate = parseFloat(newPayment.commissionRate) || 0;
        const commission = ownerLoadAmount * (commissionRate / 100);
        return ownerLoadAmount - commission;
        
      default:
        return parseFloat(newPayment.basePay) || 0;
    }
  };

  const calculateCommission = () => {
    if (newPayment.paymentCalculationType === 'owner_operator') {
      const loadAmount = parseFloat(newPayment.loadAmount) || 0;
      const commissionRate = parseFloat(newPayment.commissionRate) || 0;
      return loadAmount * (commissionRate / 100);
    }
    return 0;
  };

  const calculateNetPayment = () => {
    const totalDeductions = Object.values(newPayment.deductions).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
    const totalBonuses = Object.values(newPayment.bonuses).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
    const baseAmount = calculateBasePay();
    return Math.max(0, baseAmount - totalDeductions + totalBonuses);
  };

  const validatePaymentForm = () => {
    // Basic required fields
    if (!newPayment.recipientName || !newPayment.payPeriodStart || !newPayment.payPeriodEnd || !newPayment.paymentDate) {
      return 'Please fill in recipient name, pay period dates, and payment date';
    }

    // Validation based on calculation type
    const { paymentCalculationType } = newPayment;
    
    switch (paymentCalculationType) {
      case 'percentage':
        if (!newPayment.loadAmount || parseFloat(newPayment.loadAmount) <= 0) {
          return 'Please enter a valid load amount for percentage-based payment';
        }
        if (!newPayment.percentageRate || parseFloat(newPayment.percentageRate) <= 0) {
          return 'Please enter a valid percentage rate';
        }
        break;
        
      case 'flat_rate':
        if (!newPayment.salaryAmount || parseFloat(newPayment.salaryAmount) <= 0) {
          return 'Please enter a valid salary amount';
        }
        break;
        
      case 'mileage':
        if (!newPayment.totalMiles || parseFloat(newPayment.totalMiles) <= 0) {
          return 'Please enter valid total miles';
        }
        if (!newPayment.mileageRate || parseFloat(newPayment.mileageRate) <= 0) {
          return 'Please enter a valid mileage rate';
        }
        break;
        
      case 'owner_operator':
        if (!newPayment.loadAmount || parseFloat(newPayment.loadAmount) <= 0) {
          return 'Please enter a valid load amount for owner operator payment';
        }
        if (newPayment.commissionRate === undefined || parseFloat(newPayment.commissionRate) < 0) {
          return 'Please enter a valid commission rate';
        }
        break;
        
      default:
        if (!newPayment.basePay || parseFloat(newPayment.basePay) <= 0) {
          return 'Please enter a valid base pay amount';
        }
    }
    
    return null; // No validation errors
  };

  const handleViewPayment = (payment) => {
    setViewingPayment(payment);
    setShowViewPaymentModal(true);
  };

  const toggleActionMenu = (paymentId) => {
    setOpenActionMenu(prev => (prev === paymentId ? null : paymentId));
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    const defaults = getDefaultPaymentForm();
    setNewPayment({
      ...defaults,
      recipientName: payment.recipientName || defaults.recipientName,
      recipientType: payment.recipientType || defaults.recipientType,
      paymentType: payment.paymentType || defaults.paymentType,
      paymentCalculationType: payment.paymentCalculationType || defaults.paymentCalculationType,
      payPeriodStart: payment.payPeriodStart || defaults.payPeriodStart,
      payPeriodEnd: payment.payPeriodEnd || defaults.payPeriodEnd,
      paymentDate: payment.paymentDate || payment.payDate || payment.payPeriodEnd || defaults.paymentDate,
      paymentMethod: payment.paymentMethod || defaults.paymentMethod,
      status: payment.status || defaults.status,
      loadCount: payment.loadCount ?? defaults.loadCount,
      totalMiles: payment.totalMiles ?? defaults.totalMiles,
      grossRevenue: payment.grossRevenue ?? defaults.grossRevenue,
      netRevenue: payment.netRevenue ?? defaults.netRevenue,
      loadAmount: payment.loadAmount ?? defaults.loadAmount,
      percentageRate: payment.percentageRate ?? defaults.percentageRate,
      salaryAmount: payment.salaryAmount ?? defaults.salaryAmount,
      mileageRate: payment.mileageRate ?? defaults.mileageRate,
      commissionRate: payment.commissionRate ?? defaults.commissionRate,
      basePay: payment.basePay ?? defaults.basePay,
      deductions: {
        ...defaults.deductions,
        ...(payment.deductions || {})
      },
      bonuses: {
        ...defaults.bonuses,
        ...(payment.bonuses || {})
      },
      notes: payment.notes || defaults.notes
    });
    setShowEditPaymentModal(true);
  };

  const tabs = [
    { id: 'overview', name: 'Overview', description: 'Key metrics & trends', icon: BarChart3 },
    { id: 'revenue', name: 'Revenue', description: 'Revenue analysis', icon: DollarSign },
    { id: 'costs', name: 'Costs', description: 'Cost breakdown & trends', icon: Receipt },
    { id: 'payments', name: 'Payment History', description: 'Pay & settlements', icon: History }
  ];

  const companyProfile = {
    name: 'Roadway Logistics Co.',
    address: '123 Logistics Way, Chicago, IL 60607',
    phone: '(312) 555-0198',
    email: 'payroll@roadwaylogistics.com'
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-payment-action-menu]')) {
        setOpenActionMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleUpdatePayment = async () => {
    const validationError = validatePaymentForm();
    if (validationError) {
      alert(validationError);
      return;
    }

    if (!editingPayment) {
      return;
    }

    setIsSavingPayment(true);

    try {
      const paymentDate = newPayment.paymentDate || newPayment.payPeriodEnd || new Date().toISOString().slice(0, 10);
      const paymentData = {
        ...newPayment,
        paymentDate
      };

      const result = await paymentService.updatePayment(editingPayment.id, paymentData);

      // Calculate financial impact
      const { netPayment } = getPaymentFinancials(paymentData);

      // Trigger financial refresh
      onPaymentUpdated({ ...result, totalAmount: netPayment });

      // Show notification
      setFinancialNotification({
        type: 'payment_updated',
        title: 'Payment Updated',
        message: `Payment ${editingPayment.paymentNumber || editingPayment.id} has been updated`,
        financialImpact: {
          expense: netPayment,
          netImpact: -netPayment
        }
      });

      await fetchPaymentsData();
      setShowEditPaymentModal(false);
      setEditingPayment(null);
      setNewPayment(getDefaultPaymentForm());
    } catch (error) {
      console.error('Failed to update payment:', error);
      alert(
        typeof error.getUserFriendlyMessage === 'function'
          ? error.getUserFriendlyMessage()
          : error.message || 'Unable to update payment'
      );
    } finally {
      setIsSavingPayment(false);
    }
  };

  const handleQuickStatusUpdate = async (paymentId, newStatus) => {
    if (!paymentId || !newStatus) {
      return;
    }

    try {
      await paymentService.updatePaymentStatus(paymentId, newStatus);
      await fetchPaymentsData();
    } catch (error) {
      console.error('Failed to update payment status:', error);
      alert(
        typeof error.getUserFriendlyMessage === 'function'
          ? error.getUserFriendlyMessage()
          : error.message || 'Unable to update payment status'
      );
    }
  };

  const filteredPayments = getFilteredPaymentData();

  const handleAddPayment = async () => {
    const validationError = validatePaymentForm();
    if (validationError) {
      alert(validationError);
      return;
    }

    setIsSavingPayment(true);

    try {
      const paymentDate = newPayment.paymentDate || newPayment.payPeriodEnd || new Date().toISOString().slice(0, 10);
      const paymentData = {
        ...newPayment,
        paymentDate
      };

      const result = await paymentService.createPayment(paymentData);

      // Calculate financial impact
      const { totalDeductions, totalBonuses, calculatedGross, netPayment } = getPaymentFinancials(paymentData);

      // Trigger financial refresh
      onPaymentCreated({ ...result, totalAmount: netPayment });

      // Show notification
      setFinancialNotification({
        type: 'payment_created',
        title: 'Payment Created',
        message: `Payment to ${newPayment.recipientName} has been created successfully`,
        financialImpact: {
          expense: netPayment,
          netImpact: -netPayment
        }
      });

      await fetchPaymentsData();
      setNewPayment(getDefaultPaymentForm());
      setShowAddPaymentModal(false);
    } catch (error) {
      console.error('Failed to create payment:', error);
      alert(
        typeof error.getUserFriendlyMessage === 'function'
          ? error.getUserFriendlyMessage()
          : error.message || 'Unable to create payment'
      );
    } finally {
      setIsSavingPayment(false);
    }
  };



  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || Number.isNaN(amount)) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) {
      return 'N/A';
    }

    const parsed = new Date(dateStr);
    if (Number.isNaN(parsed.getTime())) {
      return dateStr;
    }

    return parsed.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const toTitleCase = (value = '') =>
    value
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())
      .trim();

  const getPaymentFinancials = (payment = {}) => {
    const deductions = payment.deductions || {};
    const bonuses = payment.bonuses || {};
    const totalDeductions = Object.values(deductions).reduce((sum, val) => sum + (Number(val) || 0), 0);
    const totalBonuses = Object.values(bonuses).reduce((sum, val) => sum + (Number(val) || 0), 0);
    const basePayValue = Number(payment.basePay ?? payment.calculatedBasePay);
    const calculatedGross = Number.isFinite(basePayValue)
      ? basePayValue
      : Number(payment.grossRevenue)
        || Number(payment.loadAmount)
        || Number(payment.amount)
        || 0;
    const netAmount = Number(payment.netPayment ?? payment.amount);
    const netPayment = Number.isFinite(netAmount)
      ? netAmount
      : calculatedGross - totalDeductions + totalBonuses;

    return { totalDeductions, totalBonuses, calculatedGross, netPayment };
  };

  const buildPaystubHtml = (payment) => {
    const { totalDeductions, totalBonuses, calculatedGross, netPayment } = getPaymentFinancials(payment);
    const issueDate = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    const paymentIdentifier = payment.paymentNumber || payment.id || 'Payment';
    const payPeriod = payment.payPeriodStart && payment.payPeriodEnd
      ? `${formatDateDisplay(payment.payPeriodStart)} - ${formatDateDisplay(payment.payPeriodEnd)}`
      : 'N/A';
    const paymentDate = formatDateDisplay(payment.paymentDate || payment.payDate);
    const calculationMethod = payment.paymentCalculationType
      ? toTitleCase(payment.paymentCalculationType)
      : toTitleCase(payment.paymentType || 'Payment');

    const deductionRows = Object.entries(payment.deductions || {})
      .filter(([, value]) => Number(value) > 0)
      .map(([key, value]) => `
        <tr>
          <td>${toTitleCase(key)}</td>
          <td class="text-right text-red">-${formatCurrency(Number(value) || 0)}</td>
        </tr>
      `)
      .join('') || '<tr><td colspan="2" class="text-center muted">No deductions</td></tr>';

    const bonusRows = Object.entries(payment.bonuses || {})
      .filter(([, value]) => Number(value) > 0)
      .map(([key, value]) => `
        <tr>
          <td>${toTitleCase(key)}</td>
          <td class="text-right text-green">+${formatCurrency(Number(value) || 0)}</td>
        </tr>
      `)
      .join('') || '<tr><td colspan="2" class="text-center muted">No bonuses</td></tr>';

    return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Paystub - ${paymentIdentifier}</title>
    <style>
      * { box-sizing: border-box; }
      body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; background: #f3f4f6; margin: 0; padding: 32px; color: #111827; }
      .paystub { background: #ffffff; border-radius: 16px; padding: 32px; max-width: 900px; margin: 0 auto; box-shadow: 0 15px 35px rgba(15, 23, 42, 0.08); }
      header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #e5e7eb; padding-bottom: 16px; margin-bottom: 24px; }
      header h1 { margin: 0; font-size: 24px; }
      .company-info { font-size: 14px; line-height: 1.5; color: #4b5563; text-align: right; }
      .section { margin-bottom: 24px; }
      .section-title { font-size: 16px; font-weight: 600; color: #1f2937; margin-bottom: 8px; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; }
      table { width: 100%; border-collapse: collapse; margin-top: 12px; }
      th, td { text-align: left; padding: 8px 0; font-size: 14px; }
      .summary-table th { color: #6b7280; font-weight: 500; }
      .summary-table td { font-weight: 600; }
      .text-right { text-align: right; }
      .text-center { text-align: center; }
      .muted { color: #9ca3af; font-style: italic; }
      .text-red { color: #dc2626; }
      .text-green { color: #059669; }
    </style>
  </head>
  <body>
    <div class="paystub">
      <header>
        <div>
          <h1>Paystub</h1>
          <div>Issued ${issueDate}</div>
        </div>
        <div class="company-info">
          <strong>${companyProfile.name}</strong><br />
          ${companyProfile.address}<br />
          ${companyProfile.phone}<br />
          ${companyProfile.email}
        </div>
      </header>

      <div class="section">
        <div class="section-title">Payee Information</div>
        <table>
          <tr>
            <th>Name</th>
            <td>${payment.recipientName || 'N/A'}</td>
            <th>Role</th>
            <td>${toTitleCase(payment.recipientType || 'N/A')}</td>
          </tr>
          <tr>
            <th>Payment ID</th>
            <td>${paymentIdentifier}</td>
            <th>Payment Date</th>
            <td>${paymentDate}</td>
          </tr>
          <tr>
            <th>Pay Period</th>
            <td>${payPeriod}</td>
            <th>Method</th>
            <td>${toTitleCase(payment.paymentMethod || 'Direct Deposit')}</td>
          </tr>
          <tr>
            <th>Calculation Method</th>
            <td colspan="3">${calculationMethod}</td>
          </tr>
        </table>
      </div>

      <div class="section">
        <div class="section-title">Payment Summary</div>
        <table class="summary-table">
          <tr>
            <th>Gross Earnings</th>
            <td>${formatCurrency(calculatedGross)}</td>
          </tr>
          <tr>
            <th>Total Bonuses</th>
            <td>${formatCurrency(totalBonuses)}</td>
          </tr>
          <tr>
            <th>Total Deductions</th>
            <td>${formatCurrency(totalDeductions)}</td>
          </tr>
          <tr>
            <th>Net Pay</th>
            <td>${formatCurrency(netPayment)}</td>
          </tr>
        </table>
      </div>

      <div class="section">
        <div class="section-title">Deductions</div>
        <table>
          ${deductionRows}
        </table>
      </div>

      <div class="section">
        <div class="section-title">Bonuses</div>
        <table>
          ${bonusRows}
        </table>
      </div>

      <div class="section">
        <div class="section-title">Certification</div>
        <p style="font-size:14px; color:#4b5563; line-height:1.6;">
          This document certifies that the payee listed above is active with ${companyProfile.name} and received the net payment shown for the pay period indicated. Present this document as proof of income or employment when required.
        </p>
      </div>
    </div>
  </body>
</html>`;
  };

  const handlePrintPaystub = (payment) => {
    if (typeof window === 'undefined' || !payment) {
      return;
    }

    const printWindow = window.open('', '_blank', 'width=900,height=1100');
    if (!printWindow) {
      alert('Please allow pop-ups to print the paystub.');
      return;
    }

    printWindow.document.write(buildPaystubHtml(payment));
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4 sm:mb-6 md:mb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-2 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">Financial Dashboard</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">Complete financial overview and profitability tracking</p>
            </div>
          </div>
        </div>


        {/* Tab Navigation */}
        <FinancialTabsNav
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <OverviewTab
            financialData={financialData}
            formatCurrency={formatCurrency}
          />
        )}
        {activeTab === 'revenue' && (
          <RevenueTab
            formatCurrency={formatCurrency}
          />
        )}
        {activeTab === 'costs' && (
          <CostsTab
            formatCurrency={formatCurrency}
          />
        )}
        {activeTab === 'payments' && (
          <PaymentsTab
            financialData={financialData}
            formatCurrency={formatCurrency}
            paymentFilters={paymentFilters}
            setPaymentFilters={setPaymentFilters}
            setShowAddPaymentModal={setShowAddPaymentModal}
            filteredPayments={filteredPayments}
            handleViewPayment={handleViewPayment}
            handleEditPayment={handleEditPayment}
            handleQuickStatusUpdate={handleQuickStatusUpdate}
            toggleActionMenu={toggleActionMenu}
            openActionMenu={openActionMenu}
            setOpenActionMenu={setOpenActionMenu}
            handlePrintPaystub={handlePrintPaystub}
            getPaymentFinancials={getPaymentFinancials}
            formatDateDisplay={formatDateDisplay}
            toTitleCase={toTitleCase}
            paymentsLoading={paymentsLoading}
            paymentsError={paymentsError}
            pendingPayments={pendingPayments}
            pagination={paymentsPagination}
            onRefreshPayments={fetchPaymentsData}
            onPageChange={handlePaymentsPageChange}
          />
        )}
        {/* Add Payment Modal */}
        <AddPaymentModal
          isOpen={showAddPaymentModal}
          onClose={() => setShowAddPaymentModal(false)}
          newPayment={newPayment}
          setNewPayment={setNewPayment}
          handleAddPayment={handleAddPayment}
          calculateBasePay={calculateBasePay}
          calculateCommission={calculateCommission}
          calculateNetPayment={calculateNetPayment}
          formatCurrency={formatCurrency}
          isSubmitting={isSavingPayment}
        />
      {/* Edit Payment Modal */}
        <EditPaymentModal
          isOpen={showEditPaymentModal}
          onClose={() => {
            setShowEditPaymentModal(false);
            setEditingPayment(null);
          }}
          editingPayment={editingPayment}
          newPayment={newPayment}
          setNewPayment={setNewPayment}
          handleUpdatePayment={handleUpdatePayment}
          calculateBasePay={calculateBasePay}
          calculateCommission={calculateCommission}
          calculateNetPayment={calculateNetPayment}
          formatCurrency={formatCurrency}
          isSubmitting={isSavingPayment}
        />
      {/* View Payment Modal */}
        <ViewPaymentModal
          isOpen={showViewPaymentModal}
          onClose={() => {
            setShowViewPaymentModal(false);
            setViewingPayment(null);
          }}
          viewingPayment={viewingPayment}
          onEdit={handleEditPayment}
          handlePrintPaystub={handlePrintPaystub}
          formatCurrency={formatCurrency}
          formatDateDisplay={formatDateDisplay}
          toTitleCase={toTitleCase}
        />

      {/* Financial Notification */}
      <FinancialNotification
        notification={financialNotification}
        onClose={() => setFinancialNotification(null)}
      />

      </div>
    </div>
  );
};

export default FinancialDashboard;
